import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import type { TManagerApplicationRow } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/types.ts';
import { MANAGER_APPLICATION_ASSIGNMENT_MISSING_ID_TEXT } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/constants.ts';
import {
  buildCovenantsManagerApplicationPath,
  getCovenantsRequestErrorMessage,
} from '~/modules/covenants/utils.ts';
import {
  assignSupervisorApplication,
  fetchSupervisorApplications,
  unassignSupervisorApplication,
} from '~/store/covenants/thunks/supervisorApplicationThunks.ts';
import type { TSupervisorApplicationsRequest } from '~/store/covenants/types.ts';
import type { AppDispatch } from '~/store/store.ts';

type TManagerAssignmentAction = {
  applicationId: string;
  action: 'assign' | 'unassign';
};

type TUseManagerApplicationAssignmentParams = {
  supervisorApplicationsRequest: TSupervisorApplicationsRequest;
  managerApplicationsListSearch: string;
  onError: (message: string | null) => void;
};

export const useManagerApplicationAssignment = ({
  supervisorApplicationsRequest,
  managerApplicationsListSearch,
  onError,
}: TUseManagerApplicationAssignmentParams) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [assignmentAction, setAssignmentAction] = useState<TManagerAssignmentAction | null>(null);
  const [assignedByCurrentManagerIds, setAssignedByCurrentManagerIds] = useState<string[]>([]);

  const handleAssignmentAction = useCallback(
    async (application: TManagerApplicationRow, action: 'assign' | 'unassign') => {
      const applicationId = application.id;

      if (!applicationId) {
        onError(MANAGER_APPLICATION_ASSIGNMENT_MISSING_ID_TEXT);
        return;
      }

      onError(null);
      setAssignmentAction({
        applicationId,
        action,
      });

      let shouldNavigateToApplication = false;

      try {
        const payload = {
          applicationId,
          version: application.version,
        };

        if (action === 'assign') {
          await dispatch(assignSupervisorApplication(payload)).unwrap();
          setAssignedByCurrentManagerIds((previousState) =>
            previousState.includes(applicationId)
              ? previousState
              : [...previousState, applicationId]
          );
          shouldNavigateToApplication = true;
        } else {
          await dispatch(unassignSupervisorApplication(payload)).unwrap();
          setAssignedByCurrentManagerIds((previousState) =>
            previousState.filter((id) => id !== applicationId)
          );
          await dispatch(
            fetchSupervisorApplications({ ...supervisorApplicationsRequest })
          ).unwrap();
        }
      } catch (error) {
        onError(getCovenantsRequestErrorMessage(error));
      } finally {
        setAssignmentAction(null);
      }

      if (shouldNavigateToApplication) {
        navigate(
          buildCovenantsManagerApplicationPath(applicationId, managerApplicationsListSearch)
        );
      }
    },
    [dispatch, managerApplicationsListSearch, navigate, onError, supervisorApplicationsRequest]
  );

  const handleAssignApplication = useCallback(
    (application: TManagerApplicationRow) => {
      void handleAssignmentAction(application, 'assign');
    },
    [handleAssignmentAction]
  );

  const handleUnassignApplication = useCallback(
    (application: TManagerApplicationRow) => {
      void handleAssignmentAction(application, 'unassign');
    },
    [handleAssignmentAction]
  );

  return {
    assignmentAction,
    assignedByCurrentManagerIds,
    handleAssignApplication,
    handleUnassignApplication,
  };
};
